import { useCallback, useState } from 'react'

import useEasytabsVisibility from 'Pods/Shared/Hooks/useEasytabsVisibility'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import I18n from 'Pods/Shared/I18n'

import useCancelFee from './useCancelFee'
import useSubscription from './useSubscription'
import useSubscriptionSettingsTracking from './useSubscriptionSettingsTracking'
import useSwitchPlanPreview from './useSwitchPlanPreview'
import { PAUSE_ERROR_KEYS, PLAN_TYPE, SUBSCRIPTION_STATUS } from '../constants'

const USAGE_DISABLED_STATUSES = [
  SUBSCRIPTION_STATUS.CANCELED,
  SUBSCRIPTION_STATUS.PAUSED,
  SUBSCRIPTION_STATUS.REDEMPTION,
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.SUSPENDED,
]

// The fee preview returns formatted amounts (e.g. "USD 124.97"); keep only the numeric value.
const parseAmount = (value) => {
  if (typeof value === 'number') return value

  return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0
}

function useSubscriptionManagement({
  subscription = null,
  locale = 'en',
  subscriptionV16Enabled = false,
  subscriptionPauseEnabled = false,
}) {
  const [subscriptionData, setSubscriptionData] = useState(subscription)
  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState(false)
  const [isPauseConfirmationOpen, setIsPauseConfirmationOpen] = useState(false)
  const [cancelConfirmation, setCancelConfirmation] = useState(null)
  const [resumeRequested, setResumeRequested] = useState(false)
  const {
    feeData, isFeeLoading, feeError, fetchFee, resetFeeData,
  } = useCancelFee({ subscriptionId: subscriptionData?.id })
  const {
    previewData, isPreviewLoading, fetchPreview, resetPreview,
  } = useSwitchPlanPreview()
  const { showError } = useNotifications()

  // Plan-type fact, independent of the flag: monthly subscribers (incl. ones from the v1.6
  // window) must skip the early-cancellation fee fetch even when the flag is off.
  const isMonthlyPlan = subscriptionData?.planType === 'monthly'

  // The v1.6 cancel flow (survey step, consequences screen, confirmation modal) ships behind
  // subscription_pause_enabled on top of the already-rolled-out subscription_v1_6, so the new
  // screens reach only the pause rollout. Mirrors cancel_flow_v16_enabled? in the API controller.
  const isCancelFlowV16Enabled = subscriptionV16Enabled && subscriptionPauseEnabled

  const {
    trackSectionView,
    trackCancelClick,
    trackRenewClick,
    trackRenewSuccess,
    trackRenewFailure,
    trackReactivateClick,
    trackReactivateSuccess,
    trackReactivateFailure,
    trackRetryClick,
    trackPauseScheduled,
    trackPauseFailure,
    trackPauseResumeClick,
    trackPauseResumeSuccess,
    trackPauseResumeFailure,
  } = useSubscriptionSettingsTracking(subscriptionData?.tier)

  const isExpired = subscriptionData?.status === SUBSCRIPTION_STATUS.EXPIRED

  const translate = (key, options = {}) =>
    I18n.t(key, { ns: 'rails', lng: locale, ...options })

  const handleTabVisible = useCallback(() => {
    if (subscriptionData) {
      trackSectionView()
    }
  }, [subscriptionData, trackSectionView])

  useEasytabsVisibility('tab-settings-subscription', handleTabVisible)

  const handleRestoreSuccess = useCallback(
    ({ renewalScheduled = false } = {}) => {
      if (!renewalScheduled) {
        setSubscriptionData((prev) => ({
          ...prev,
          canceledAt: null,
          renewsOn: prev.periodEndDate,
        }))
      }

      if (isExpired) {
        trackReactivateSuccess(subscriptionData?.id)
      } else {
        trackRenewSuccess(subscriptionData?.id)
      }
    },
    [isExpired, subscriptionData?.id, trackReactivateSuccess, trackRenewSuccess]
  )

  const handleRestoreError = useCallback(
    (errorMessage) => {
      if (isExpired) {
        trackReactivateFailure(subscriptionData?.id, errorMessage)
      } else {
        trackRenewFailure(subscriptionData?.id, errorMessage)
      }
    },
    [isExpired, subscriptionData?.id, trackReactivateFailure, trackRenewFailure]
  )

  const handleRedirectToPaymentForm = useCallback((redirectUrl) => {
    const fallbackUrl = '/profile/subscription/payment-form'
    window.location.assign(redirectUrl || fallbackUrl)
  }, [])

  const handleCancelPlanSwitchSuccess = useCallback(() => {
    setSubscriptionData((prev) => ({
      ...prev,
      scheduledPlanSwitchTo: null,
      scheduledPlanSwitchAt: null,
      // Solidgate sets canceled_at via webhook when the switch is committed.
      // Clear it optimistically on cancel since restore_subscription undoes that.
      canceledAt: null,
      renewsOn: prev.periodEndDate,
    }))
  }, [])

  const pauseErrorMessage = useCallback((errorCode) => {
    const key = PAUSE_ERROR_KEYS.includes(errorCode) ? errorCode : 'generic'
    return I18n.t(`subscription_management.pause.errors.${key}`, { ns: 'rails', lng: locale })
  }, [locale])

  const handlePauseError = useCallback((errorCode) => {
    trackPauseFailure(subscriptionData?.id, errorCode)
  }, [trackPauseFailure, subscriptionData?.id])

  const handleResumeError = useCallback((errorCode) => {
    trackPauseResumeFailure(subscriptionData?.id, errorCode)
  }, [trackPauseResumeFailure, subscriptionData?.id])

  // The confirmation is fed by the pre-submission fee preview (fetched when the modal
  // opened) — cancel_subscription answers an empty 200 until the backend implements the
  // authoritative cancellation payload (86car5fhk). Monthly plans skip the fee fetch, so
  // they get the no-fee/no-date confirmation variant.
  const handleCancelSuccess = useCallback(() => {
    setCancelConfirmation({
      accessUntil: feeData?.access_until || null,
      cancelFee: parseAmount(feeData?.cancel_fee),
    })
  }, [feeData])

  const {
    isCancelModalOpen,
    isLoading: isRestoreLoading,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    handleRenewSubscription,
    handleReactivateSubscription,
    handleRetryPayment,
    retryState,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    handlePauseSubscription,
    isPauseLoading,
    handleResumeSubscription,
    isResumeLoading,
  } = useSubscription({
    subscriptionData,
    messages: {
      cancelError: translate('subscription_management.cancel_modal.error'),
      restoreSuccess: translate('subscription_management.restore.success'),
      restoreError: translate('subscription_management.restore.error'),
      retryError: translate('subscription_management.retry_payment.error.default'),
      cancelPlanSwitchSuccess: translate('subscription_management.scheduled_switch_banner.cancel_success'),
      cancelPlanSwitchError: translate('subscription_management.scheduled_switch_banner.cancel_error'),
      pauseError: pauseErrorMessage,
      resumeError: pauseErrorMessage,
    },
    onCancelSuccess: isCancelFlowV16Enabled ? handleCancelSuccess : null,
    onRestoreSuccess: handleRestoreSuccess,
    onRestoreError: handleRestoreError,
    onRedirectToPaymentForm: handleRedirectToPaymentForm,
    onCancelPlanSwitchSuccess: handleCancelPlanSwitchSuccess,
    onPauseError: handlePauseError,
    onResumeError: handleResumeError,
  })

  // Eligibility mirrors the backend guard: rolled out to this user, active, no cancellation
  // scheduled, no pause scheduled. Allowance exhaustion is only known server-side and
  // surfaces as a 422. Resuming an existing pause is deliberately not gated, so a user who
  // paused while enrolled keeps the control if the rollout flag is turned off.
  const showPauseOffer =
    subscriptionPauseEnabled &&
    subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE &&
    !subscriptionData?.canceledAt &&
    !subscriptionData?.userPauseScheduledAt &&
    !subscriptionData?.scheduledPlanSwitchTo

  const handleCancelClick = useCallback(async () => {
    trackCancelClick()
    if (!isMonthlyPlan) {
      if (showPauseOffer) {
        // The modal opens on the pause offer, which needs no fee data - prefetch in the
        // background instead of delaying the offer; the cancel step keeps submit blocked
        // until the fee settles (isFeeLoading below).
        fetchFee()
      } else {
        await fetchFee()
      }
    }
    handleOpenCancelModal()
  }, [trackCancelClick, fetchFee, handleOpenCancelModal, isMonthlyPlan, showPauseOffer])

  const handlePauseRequest = useCallback(async () => {
    const succeeded = await handlePauseSubscription()
    if (!succeeded) return

    trackPauseScheduled(subscriptionData?.id)
    resetFeeData()
    handleCloseCancelModal()
    setIsPauseConfirmationOpen(true)
  }, [
    handlePauseSubscription,
    trackPauseScheduled,
    subscriptionData?.id,
    resetFeeData,
    handleCloseCancelModal,
  ])

  // The pause is persisted server-side (user_pause_scheduled_at) before Solidgate confirms,
  // so a full reload renders the scheduled state from fresh props.
  const handlePauseConfirmationDone = useCallback(() => {
    setIsPauseConfirmationOpen(false)
    window.location.reload()
  }, [])

  // Reload renders the cancelled state from fresh props, matching the flag-off path.
  const handleCancelConfirmationDone = useCallback(() => {
    window.location.reload()
  }, [])

  const handleResumePauseClick = useCallback(async () => {
    trackPauseResumeClick(subscriptionData?.id)
    const succeeded = await handleResumeSubscription()
    if (!succeeded) return

    trackPauseResumeSuccess(subscriptionData?.id)
    if (subscriptionData?.status === SUBSCRIPTION_STATUS.PAUSED) {
      // A live pause resumes only when the Solidgate webhook lands; show progress
      // instead of reloading into an unchanged paused page.
      setResumeRequested(true)
    } else {
      // A scheduled pause is deleted synchronously server-side; reload shows it gone.
      window.location.reload()
    }
  }, [
    trackPauseResumeClick,
    subscriptionData?.id,
    subscriptionData?.status,
    handleResumeSubscription,
    trackPauseResumeSuccess,
  ])

  const handleCloseCancel = useCallback(() => {
    resetFeeData()
    handleCloseCancelModal()
  }, [resetFeeData, handleCloseCancelModal])

  const handleRenewClick = useCallback(() => {
    trackRenewClick(subscriptionData?.id)
    handleRenewSubscription()
  }, [subscriptionData?.id, trackRenewClick, handleRenewSubscription])

  const handleReactivateClick = useCallback(() => {
    trackReactivateClick(subscriptionData?.id)
    handleReactivateSubscription()
  }, [subscriptionData?.id, trackReactivateClick, handleReactivateSubscription])

  const handleRetryClick = useCallback(() => {
    trackRetryClick(subscriptionData?.id)
    handleRetryPayment()
  }, [subscriptionData?.id, trackRetryClick, handleRetryPayment])

  const handleSwitchSuccess = useCallback((responseData) => {
    const scheduledTo = responseData?.scheduled_plan_switch_to ?? null
    setSubscriptionData((prev) => ({
      ...prev,
      scheduledPlanSwitchTo: scheduledTo,
      scheduledPlanSwitchAt: responseData?.scheduled_plan_switch_at ?? null,
      // Monthly→Annual takes effect immediately — reflect the new plan in the UI right away
      ...(scheduledTo === PLAN_TYPE.YEARLY && { planType: PLAN_TYPE.YEARLY, billingCycle: PLAN_TYPE.YEARLY }),
    }))
  }, [])

  const handleSwitchPlanClick = useCallback(async () => {
    if (isPreviewLoading || subscriptionData?.scheduledPlanSwitchTo) return
    const currentPlan = subscriptionData?.planType === PLAN_TYPE.MONTHLY
      ? PLAN_TYPE.MONTHLY
      : PLAN_TYPE.YEARLY
    const targetPlan = currentPlan === PLAN_TYPE.YEARLY ? PLAN_TYPE.MONTHLY : PLAN_TYPE.YEARLY
    const data = await fetchPreview({ to: targetPlan })
    if (data) {
      setIsSwitchPlanModalOpen(true)
    } else {
      showError(translate('subscription_management.switch_plan_modal.preview_toast_error'))
    }
  }, [
    isPreviewLoading,
    subscriptionData?.planType,
    subscriptionData?.scheduledPlanSwitchTo,
    fetchPreview,
    showError,
    translate,
  ])

  const handleCloseSwitchPlanModal = useCallback(() => {
    setIsSwitchPlanModalOpen(false)
    resetPreview()
  }, [resetPreview])

  const isUsageDisabled = USAGE_DISABLED_STATUSES.includes(subscriptionData?.status)

  // When Annual→Monthly switch is scheduled, Solidgate internally cancels the subscription
  // (force: false), which sets canceled_at via webhook. Override for display so the card
  // shows Active instead of PENDING_CANCEL while the user is still in the committed period.
  const subscriptionForDisplay = subscriptionData?.scheduledPlanSwitchTo === PLAN_TYPE.MONTHLY
    ? { ...subscriptionData, canceledAt: null, renewsOn: subscriptionData.periodEndDate }
    : subscriptionData

  return {
    translate,
    subscriptionData,
    subscriptionForDisplay,
    isUsageDisabled,
    feeData,
    feeError,
    isFeeLoading,
    isCancelModalOpen,
    isRestoreLoading,
    retryState,
    handleCancelClick,
    handleCloseCancel,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    handleRenewClick,
    handleReactivateClick,
    handleRetryClick,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    isCancelFlowV16Enabled,
    showPauseOffer,
    handlePauseRequest,
    isPauseLoading,
    isPauseConfirmationOpen,
    handlePauseConfirmationDone,
    cancelConfirmation,
    handleCancelConfirmationDone,
    handleResumePauseClick,
    isResumeLoading,
    resumeRequested,
    isSwitchPlanModalOpen,
    previewData,
    handleSwitchPlanClick,
    handleCloseSwitchPlanModal,
    handleSwitchSuccess,
  }
}

export default useSubscriptionManagement
